import { useCallback } from 'react';

import { type CategoryNode } from '../../category-tree';
import {
  type SelectedSchoolInfo,
  type SubjectFormValues,
  type SubjectTree,
} from '../types';

import { useSelectSubjectNode } from './useSelectSubjectNode';
import { useSelectSubSubjectNode } from './useSelectSubSubjectNode';
import { useSubjectNodeList } from './useSubjectNodeList';
import { useValidateSubjectCategory } from './useValidateSubjectCategory';

/**
 * 과목 정보 관리 hook
 *
 * 과목(subject)과 하위 과목(subSubject) 선택을 관리합니다.
 * 선택된 학교 정보(parentId)에 따라 과목 목록이 필터링됩니다.
 */
export const useSubjectCategorySelector = ({
  subjectFormValues,
  subjectTree,
  selectedSchoolInfo,
}: {
  subjectFormValues: SubjectFormValues;
  subjectTree: SubjectTree;
  /** 상위에서 선택된 학교 정보 */
  selectedSchoolInfo: SelectedSchoolInfo;
}) => {
  const subjectNodeList = useSubjectNodeList({
    selectedSchoolInfo,
    subjectTree,
  });

  const [selectedSubjectNode, setSelectedSubjectNode] = useSelectSubjectNode({
    subjectNodeList,
    subjectFormValues,
  });

  const [selectedSubSubjectNode, setSelectedSubSubjectNode] =
    useSelectSubSubjectNode({
      selectedSubjectNode,
      subjectFormValues,
    });

  const {
    errors,
    validate: _validate,
    clearError,
  } = useValidateSubjectCategory();

  // 과목 변경 핸들러 (하위 과목 초기화)
  const handleSelectSubject = (subjectNode?: CategoryNode) => {
    setSelectedSubjectNode(subjectNode);
    setSelectedSubSubjectNode(undefined);
  };

  const handleSelectSubSubject = (subSubjectNode?: CategoryNode) => {
    setSelectedSubSubjectNode(subSubjectNode);
  };

  // 유효성 검사
  const validate = useCallback((): boolean => {
    return _validate({ selectedSubjectNode, subjectNodeList });
  }, [selectedSubjectNode, subjectNodeList, _validate]);

  return {
    // 상태
    selectedSubjectNode,
    selectedSubSubjectNode,

    // 파생 데이터
    subjectNodes: subjectNodeList,

    // 액션
    handleSelectSubject,
    handleSelectSubSubject,

    // 에러
    errors,
    validate,
    clearError,
  };
};
